import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { FlyoutOpeningData } from '../flyout-models';
import {
  AccountCustomFont,
  AccountCustomFontFile,
  AccountCustomFontFileStyle,
  AccountCustomFontType,
} from 'app/account-settings/account-settings-models';
import { AccountSettingsService } from 'app/account-settings/account-settings.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { WidgetService } from 'app/shared/services/widget.service';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { TranslateService } from '@ngx-translate/core';
import FontHelpers from 'app/shared/helpers/font-helpers';
import { FlyoutService } from '../flyout.service';
import { Observable } from 'rxjs';
import { CurrentContextService } from 'app/core/current-context.service';
import { AccountUpgradePopupService } from 'app/account-upgrade/account-upgrade-popup/account-upgrade-popup.service';
import { TierAccess } from 'app/shared/account/tier.model';
import { SubscriptionHandlerBaseComponent } from 'app/core/subscription-handler.base';

@Component({
  selector: 'app-flyout-brand-settings',
  templateUrl: './flyout-brand-settings.component.html',
  styleUrl: './flyout-brand-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutBrandSettingsComponent extends SubscriptionHandlerBaseComponent {
  @Input() public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this.initializeData(openingData);
  }
  public savingInProgress = false;
  private customFonts: Array<AccountCustomFont>;
  public customFontFamilyName: string;
  public customFontCssLink: string;
  public fontExistsOnWebsiteSelected: boolean = true;

  public urlRegex = RegExp('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?');
  public familyNameError: string;
  public cssLinkError: string;
  public fontType: AccountCustomFontType;
  public AccountCustomFontTypes = AccountCustomFontType;
  public accountFont: AccountCustomFont;
  public editingFont: AccountCustomFont;

  public uploadFontsError: string;
  public uploadFontDragging = false;
  public fontFileIsLoading = false;
  public fontFileLoaded = false;
  private fontFileName = '';
  private fontFile;
  private readonly logger: Logger;
  public fontFiles: Array<AccountCustomFontFile> = [];
  public tierAccess: TierAccess;

  public FONT_FILE_WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];
  public FONT_FILE_STYLES = [
    { key: 'Normal', value: AccountCustomFontFileStyle.Normal },
    { key: 'Italic', value: AccountCustomFontFileStyle.Italic },
  ] as Array<{ key: any; value: AccountCustomFontFileStyle }>;

  constructor(
    private accountSettingService: AccountSettingsService,
    private snackbarService: SnackBarService,
    private widgetService: WidgetService,
    protected readonly loggerFactory: LoggerFactory,
    private translateService: TranslateService,
    private flyoutService: FlyoutService,
    private readonly currentContextService: CurrentContextService,
    private readonly accountUpgradePopupService: AccountUpgradePopupService,
  ) {
    super();
    this.logger = loggerFactory.get('AccountFontEditDialogComponent');
  }

  public initializeData(data): void {
    super.addSubscription(
      this.currentContextService.currentContextBS.subscribe((currentContext) => {
        if (currentContext) {
          this.tierAccess = currentContext.tierAccess;
        }
      }),
    );

    if (data) {
      if (data['customFonts']) {
        this.customFonts = data['customFonts'];
      } else {
        throw new Error('Custom fonts should be passed to this component');
      }
      this.editingFont = data['editingFont'];
      if (this.editingFont) {
        this.customFontFamilyName = this.editingFont.displayName;
        this.customFontCssLink = this.editingFont.cssLink;
        this.fontFiles =
          this.editingFont.fontFiles?.map((file) => ({
            ...file,
            fileName: file.url.substring(file.url.lastIndexOf('/') + 1),
          })) || [];
        this.accountFont = JSON.parse(
          JSON.stringify(this.customFonts.find((font) => font.name === this.editingFont.name)),
        );
        this.fontExistsOnWebsiteSelected = !this.editingFont.injectOnRendering;
      } else {
        this.customFontFamilyName = '';
        this.customFontCssLink = '';
        this.fontFiles = [];
        this.accountFont = null;
        this.fontExistsOnWebsiteSelected = true;
      }
    }
  }

  public validateFamilyName(): void {
    this.customFontFamilyName = this.customFontFamilyName?.trim();
    const formatFontFamilyName = FontHelpers.formatFontName(this.customFontFamilyName?.trim());
    this.familyNameError = undefined;
    let existingFonts = this.customFonts;
    if (this.editingFont) {
      existingFonts = JSON.parse(JSON.stringify(this.customFonts.filter((font) => font.id !== this.accountFont.id)));
    }
    if (!this.customFontFamilyName) {
      this.familyNameError = this.translateService.instant('generic.validation.error-required');
    } else if (existingFonts.some((font) => font.name === formatFontFamilyName)) {
      this.familyNameError = this.translateService.instant('settings.customization.validation.name-already-exists');
    } else if (this.customFontFamilyName.length < 4) {
      this.familyNameError = this.translateService.instant('generic.validation.error-minlength-4');
    } else if (/[^A-Za-z0-9-,_\"\s]/g.test(this.customFontFamilyName)) {
      this.familyNameError = this.translateService.instant('generic.validation.error-invalid-characters');
    }
  }

  public validateCssLink(): void {
    this.cssLinkError = undefined;
    this.customFontCssLink = this.customFontCssLink?.trim();
    let customFonts = this.customFonts;
    if (this.editingFont) {
      customFonts = JSON.parse(JSON.stringify(this.customFonts.filter((font) => font.id !== this.editingFont.id)));
    }
    if (this.customFontCssLink.length && this.customFontCssLink.length < 4) {
      this.cssLinkError = this.translateService.instant('generic.validation.error-minlength-4');
    } else if (this.customFontCssLink.length && !this.urlRegex.test(this.customFontCssLink)) {
      this.cssLinkError = this.translateService.instant('generic.validation.error-url');
    } else if (this.customFontCssLink.length && this.customFonts) {
      if (customFonts.find((f) => f.cssLink === this.customFontCssLink)) {
        this.cssLinkError = this.translateService.instant('settings.customization.validation.font-already-exists');
      }
    }
  }

  private validateData(): boolean {
    this.customFontFamilyName = FontHelpers.formatFontName(this.customFontFamilyName?.trim());
    this.validateFamilyName();
    if (this.customFontCssLink) {
      this.validateCssLink();
    }
    return !this.familyNameError && !this.cssLinkError && !this.uploadFontsError;
  }

  public createNewCustomFont(): void {
    if (!this.validateData()) return;
    this.savingInProgress = true;
    this.getNewCustomFont().subscribe(
      (changedOrCreatedFont) => {
        this.flyoutService.closeBrandSettinsFlyout({
          customFont: changedOrCreatedFont,
          isEdit: Boolean(this.editingFont),
        });
        this.savingInProgress = false;
        if (this.editingFont) {
          this.snackbarService.success('settings.customization.fonts.update-success');
        } else {
          this.snackbarService.success('settings.customization.fonts.create-success');
        }
        this.flyoutService.close();
      },
      () => {
        this.savingInProgress = false;
        this.snackbarService.error('generic.errorOccurred');
      },
    );
  }

  public getNewCustomFont(): Observable<AccountCustomFont> {
    this.savingInProgress = true;
    const customFontToCreate = {
      ...this.editingFont,
      name: this.customFontFamilyName,
      displayName: this.customFontFamilyName,
      cssLink: this.customFontCssLink,
      fontFiles: this.fontFiles,
      type: this.customFontCssLink
        ? AccountCustomFontType.CssLink
        : this.fontFiles?.length
          ? AccountCustomFontType.UploadedFont
          : null,
      injectOnRendering: !this.fontExistsOnWebsiteSelected,
    } as AccountCustomFont;
    if (this.editingFont) {
      return this.accountSettingService.updateCustomFont(customFontToCreate);
    } else {
      return this.accountSettingService.createCustomFont(customFontToCreate);
    }
  }

  public handleDragEnter(): void {
    this.uploadFontDragging = true;
  }

  public handleDragLeave(): void {
    this.uploadFontDragging = false;
  }

  public handleDrop(e): void {
    e.preventDefault();
    this.uploadFontDragging = false;
    this.handleInputChange(e);
  }

  public handleInputChange(e) {
    if (!this.tierAccess?.isFontUploadAllowed) {
      this.accountUpgradePopupService.openPopup('fonts');
      e.preventDefault();
      return;
    }
    this.fontFileIsLoading = true;
    const file = e.dataTransfer ? e.dataTransfer.files[0] : e.target.files[0];
    const reader = new FileReader();
    this.fontFileLoaded = false;
    this.fontFileName = file.name;
    this.fontFile = file;
    reader.onload = this.handleReaderLoaded.bind(this);
    reader.readAsDataURL(file);
    e.target.value = null;
  }

  private handleReaderLoaded(e) {
    const formData = new FormData();
    formData.set('fontFile', this.fontFile, this.fontFileName);
    this.uploadFontFile(formData);
  }

  private uploadFontFile(data?: any): void {
    this.fontFileIsLoading = true;
    const fontFileType = FontHelpers.getFontFileTypeFromName(this.fontFileName);
    this.widgetService.uploadFontFile(data).subscribe(
      (uploadResult) => {
        this.logger.info('Font file loaded success: ' + JSON.stringify(uploadResult));
        uploadResult.fileName = this.fontFileName;
        this.logger.info('Upload fileName: ' + this.fontFileName);
        this.fontFileLoaded = true;
        this.uploadFontsError = undefined;
        this.fontFileIsLoading = false;
        this.fontFiles.push(FontHelpers.createAccountCustomFileFromUrl(uploadResult.uri, fontFileType));
      },
      (error) => {
        this.fontFileIsLoading = false;
        this.logger.error('Could not load image: ' + JSON.stringify(error));
        this.snackbarService.error('settings.customization.fonts.error-upload');
      },
    );
  }

  public removeFontFile(index: number): void {
    this.fontFiles.splice(index, 1);
  }

  public checkIfUploadFontDisabled(e): void {
    if (!this.tierAccess?.isFontUploadAllowed) {
      this.accountUpgradePopupService.openPopup('fonts');
      e.preventDefault();
      return;
    }
    if (this.customFontCssLink) e.preventDefault();
  }

  public closeDialog() {
    this.flyoutService.close();
  }
}
